/**
 * Security cameras (Phase 8). A wall-mounted eye that never gets bored, never
 * investigates, and never comes to find you — it just watches, and after a beat
 * it tells everyone at once.
 *
 * Cameras are deliberately the most *composable* threat in the building rather
 * than the hardest:
 *   - They need LIGHT, like everyone else. Cutting the power (Phase 2) kills
 *     the feed AND the lights, which is the whole reason to go to the basement.
 *   - They SWEEP a fixed arc, so there is always a timing gap to walk through
 *     — a camera is a puzzle, not a dice roll.
 *   - A uniform slows them down (Phase 5): somebody is watching that monitor,
 *     and they judge what they see the same way a guard in the corridor would.
 *   - The alarm takes `alarmSec` of continuous sight, so being clipped by an
 *     arc costs you position, not the run.
 *
 * When one does trip, it alerts the WHOLE FLOOR — it's wired to the security
 * desk, so unlike a civilian nobody has to run anywhere with the news.
 *
 * Pure + headless (rule 4).
 */

import { CONFIG } from '../config';
import { exposure } from './disguise';
import { computeVisionCone } from './fov';
import type { Guard } from './guard';
import { perceivedLight, roomAt, visibilityFactor } from './light';
import { tileAt, type MapData } from './map';
import type { Player } from './player';
import { blocksVision } from './tiles';

export interface Camera {
  x: number;
  y: number;
  /** Centre of the sweep arc (unit-ish direction into the room). */
  baseDx: number;
  baseDy: number;
  /** Current facing, swung across the arc. */
  facingDx: number;
  facingDy: number;
  /** Sweep phase in radians, advanced each tick. */
  phase: number;
  /** Seconds of continuous sight banked toward tripping the alarm. */
  alarmTimer: number;
  /** Shot out / disabled — a dead camera stays dead. */
  destroyed: boolean;
}

export function createCamera(x: number, y: number, dx: number, dy: number): Camera {
  return {
    x, y,
    baseDx: dx, baseDy: dy,
    facingDx: dx, facingDy: dy,
    phase: 0,
    alarmTimer: 0,
    destroyed: false,
  };
}

/** Has this camera got power? It's on the room's breaker like everything else,
 *  so a blackout blinds it. */
export function cameraPowered(cam: Camera, map: MapData): boolean {
  if (cam.destroyed) return false;
  // A wall-mounted camera belongs to the room it looks INTO.
  const room = roomAt(map, cam.x + cam.baseDx, cam.y + cam.baseDy);
  return room?.lightsOn !== false;
}

/** Rotate a unit vector by `rad`, snapped back to the 8-way grid the sim uses. */
function swing(dx: number, dy: number, rad: number): { dx: number; dy: number } {
  const a = Math.atan2(dy, dx) + rad;
  return { dx: Math.cos(a), dy: Math.sin(a) };
}

/** What a camera did this tick. */
export interface CameraAlarm {
  camera: Camera;
  x: number;
  y: number;
}

/**
 * Advance every camera. Returns the alarms tripped this tick; the caller turns
 * each into a floor-wide alert (see `raiseAlarm`).
 */
export function updateCameras(
  cameras: Camera[],
  map: MapData,
  player: Player,
  tickDt: number,
  /** Steam blinds a lens as readily as an eye. */
  fog?: (x: number, y: number) => boolean,
): CameraAlarm[] {
  const cfg = CONFIG.securityCamera;
  const out: CameraAlarm[] = [];
  for (const cam of cameras) {
    // Sweep runs even unpowered — a dead camera still hangs there pointing
    // somewhere, and the player can't tell live from dead at a glance.
    cam.phase += tickDt * cfg.sweepRateRad;
    const swept = swing(cam.baseDx, cam.baseDy, Math.sin(cam.phase) * cfg.sweepHalfAngleRad);
    cam.facingDx = swept.dx;
    cam.facingDy = swept.dy;

    if (!cameraPowered(cam, map) || player.body.dead || player.concealedAt !== null
        || player.invisible) {
      cam.alarmTimer = 0;
      continue;
    }
    if (!sees(cam, map, player, cfg, fog)) {
      cam.alarmTimer = Math.max(0, cam.alarmTimer - tickDt * cfg.forgetRate);
      continue;
    }
    // Somebody is watching this feed, and they judge a uniform the same way a
    // guard in the corridor would — so a disguise buys time here too.
    cam.alarmTimer += tickDt * exposure(player, map, cam.x, cam.y);
    if (cam.alarmTimer >= cfg.alarmSec) {
      cam.alarmTimer = 0;
      out.push({ camera: cam, x: player.x, y: player.y });
    }
  }
  return out;
}

function sees(
  cam: Camera,
  map: MapData,
  player: Player,
  cfg: typeof CONFIG.securityCamera,
  fog?: (x: number, y: number) => boolean,
): boolean {
  const d = Math.max(Math.abs(cam.x - player.x), Math.abs(cam.y - player.y));
  if (d > cfg.range) return false;
  if (visibilityFactor(perceivedLight(map, player.x, player.y, false)) <= 0) return false;
  const fov = new Set<number>();
  computeVisionCone(
    cam.x, cam.y, cam.facingDx, cam.facingDy, cfg.range, cfg.halfAngleRad,
    (x, y) => fog?.(x, y) === true || blocksVision(tileAt(map, x, y)),
    (x, y) => fov.add(y * map.width + x),
  );
  return fov.has(player.y * map.width + player.x);
}

/**
 * A tripped camera puts EVERY live guard on the floor onto the sighting — it's
 * wired to the security desk, so nobody has to carry the news.
 */
export function raiseAlarm(guards: readonly Guard[], x: number, y: number): void {
  const cautious = CONFIG.awareness.thresholds.cautious;
  for (const g of guards) {
    if (g.body.dead) continue;
    g.investigationX = x;
    g.investigationY = y;
    g.lastKnownPlayerX = x;
    g.lastKnownPlayerY = y;
    if (g.awareness < cautious) g.awareness = cautious;
    if (g.state === 'unaware' || g.state === 'suspicious') g.state = 'cautious';
    g.alertingTimer = Math.max(g.alertingTimer, 1.5);
    g.path = null;
  }
}
